import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { NodeData } from "../types/types";
import { stripHtmlFromLabel } from "../utils/textFormatting";
import "../styles/components/CanvasizeArea.css";

type CanvasNode = {
  id: string;
  data: NodeData;
  type?: string;
};

type CanvasEdge = {
  source?: string;
  target?: string;
};

interface CanvasizeAreaProps {
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  onUpdateNodes: (updated: CanvasNode[]) => void;
}

type Position = { x: number; y: number };

const DEFAULT_RADIUS = 180;
const RING_GAP = 220;
const MAX_PER_RING = 14;
const CARD_WIDTH = 360;
const CARD_HEIGHT = 260;
const CARD_GAP = 72;
const CENTER_CARD_WIDTH = 360;
const CENTER_CARD_HEIGHT = 260;

const CanvasizeArea: React.FC<CanvasizeAreaProps> = ({
  nodes,
  edges,
  onUpdateNodes,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const breadcrumbRef = useRef<HTMLDivElement>(null);
  const ellipsisRef = useRef<HTMLSpanElement>(null);
  const breadcrumbMeasureRefs = useRef<Record<string, HTMLSpanElement | null>>(
    {},
  );

  const [centerId, setCenterId] = useState<string | null>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [visibleBreadcrumbStart, setVisibleBreadcrumbStart] = useState(0);
  const [showOverflowPopup, setShowOverflowPopup] = useState(false);
  const [breadcrumbFilter, setBreadcrumbFilter] = useState("");

  const centerNode = useMemo(() => {
    if (centerId) {
      const found = nodes.find((node) => node.id === centerId);
      if (found) return found;
    }
    return (
      nodes.find((node) => node.id === "root") ||
      nodes.find((node) => node.type === "center") ||
      nodes[0] ||
      null
    );
  }, [centerId, nodes]);

  useEffect(() => {
    if (!centerId && centerNode) {
      setCenterId(centerNode.id);
    }
  }, [centerId, centerNode]);

  const parentByChild = useMemo(() => {
    const map = new Map<string, string>();
    edges.forEach((edge) => {
      if (edge.source && edge.target) {
        map.set(edge.target, edge.source);
      }
    });
    return map;
  }, [edges]);

  const childNodes = useMemo(() => {
    if (!centerNode) return [];
    const children = edges
      .filter((edge) => edge.source === centerNode.id)
      .map((edge) => nodes.find((node) => node.id === edge.target))
      .filter(Boolean) as CanvasNode[];
    return children;
  }, [centerNode, edges, nodes]);

  const breadcrumbItems = useMemo(() => {
    if (!centerNode) return [] as CanvasNode[];
    const path: CanvasNode[] = [];
    let current: CanvasNode | undefined = centerNode;
    const visited = new Set<string>();
    while (current && !visited.has(current.id)) {
      path.unshift(current);
      visited.add(current.id);
      const parentId = parentByChild.get(current.id);
      current = parentId
        ? nodes.find((node) => node.id === parentId)
        : undefined;
    }
    return path;
  }, [centerNode, nodes, parentByChild]);

  const visibleBreadcrumbItems = useMemo(() => {
    if (visibleBreadcrumbStart <= 0) return breadcrumbItems;
    return breadcrumbItems.slice(visibleBreadcrumbStart);
  }, [breadcrumbItems, visibleBreadcrumbStart]);

  const overflowItems = useMemo(() => {
    if (visibleBreadcrumbStart <= 0) return [] as CanvasNode[];
    return breadcrumbItems.slice(0, visibleBreadcrumbStart);
  }, [breadcrumbItems, visibleBreadcrumbStart]);

  const computeRadialPosition = (index: number, total: number): Position => {
    if (total === 0) return { x: 0, y: 0 };
    const safeWidth = Math.max(containerSize.width, CARD_WIDTH * 2);
    const safeHeight = Math.max(containerSize.height, CARD_HEIGHT * 2);
    const maxRadiusX = safeWidth / 2 - CARD_WIDTH / 2 - CARD_GAP;
    const maxRadiusY = safeHeight / 2 - CARD_HEIGHT / 2 - CARD_GAP;
    const baseRadiusX = Math.max(
      DEFAULT_RADIUS,
      CENTER_CARD_WIDTH / 2 + CARD_WIDTH / 2 + CARD_GAP,
    );
    const baseRadiusY = Math.max(
      DEFAULT_RADIUS,
      CENTER_CARD_HEIGHT / 2 + CARD_HEIGHT / 2 + CARD_GAP,
    );

    const maxRadius = Math.max(160, Math.min(maxRadiusX, maxRadiusY));
    const perRing = Math.max(
      6,
      Math.min(
        MAX_PER_RING,
        Math.floor(
          (2 * Math.PI * Math.max(maxRadius, 1)) / (CARD_WIDTH + CARD_GAP),
        ),
      ),
    );
    const ringCount = Math.max(1, Math.ceil(total / perRing));
    const ring = Math.floor(index / perRing);
    const ringIndex = index % perRing;
    const itemsInRing = Math.min(perRing, total - ring * perRing);

    const stepX =
      ringCount > 1
        ? Math.max(RING_GAP, (maxRadiusX - baseRadiusX) / (ringCount - 1))
        : 0;
    const stepY =
      ringCount > 1
        ? Math.max(RING_GAP, (maxRadiusY - baseRadiusY) / (ringCount - 1))
        : 0;

    const radiusX = Math.min(maxRadiusX, baseRadiusX + ring * stepX);
    const radiusY = Math.min(maxRadiusY, baseRadiusY + ring * stepY);

    const angle = (2 * Math.PI * ringIndex) / Math.max(itemsInRing, 1);
    return {
      x: Math.round(Math.cos(angle) * radiusX),
      y: Math.round(Math.sin(angle) * radiusY),
    };
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!showOverflowPopup) return;
      const target = event.target as HTMLElement;
      if (!target.closest(".canvasize-breadcrumb-overflow")) {
        setShowOverflowPopup(false);
      }
    };

    window.addEventListener("mousedown", handleOutsideClick);
    return () => window.removeEventListener("mousedown", handleOutsideClick);
  }, [showOverflowPopup]);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const containerWidth = breadcrumbRef.current?.clientWidth || 0;
    if (!containerWidth || breadcrumbItems.length === 0) {
      setVisibleBreadcrumbStart(0);
      return;
    }

    const itemWidths = breadcrumbItems.map((item) => {
      const ref = breadcrumbMeasureRefs.current[item.id];
      return ref?.getBoundingClientRect().width || 0;
    });

    const ellipsisWidth =
      ellipsisRef.current?.getBoundingClientRect().width || 24;
    const separatorWidth = 18;

    let used = 0;
    let startIndex = breadcrumbItems.length - 1;
    let needsEllipsis = false;

    for (let i = breadcrumbItems.length - 1; i >= 0; i -= 1) {
      const extra = i < breadcrumbItems.length - 1 ? separatorWidth : 0;
      const nextUsed = used + itemWidths[i] + extra;
      const remaining = i > 0 ? ellipsisWidth + separatorWidth : 0;

      if (nextUsed + remaining <= containerWidth) {
        used = nextUsed;
        startIndex = i;
      } else {
        needsEllipsis = true;
        break;
      }
    }

    setVisibleBreadcrumbStart(needsEllipsis ? startIndex : 0);
  }, [breadcrumbItems, containerSize.width]);

  const sortedChildNodes = useMemo(() => {
    return [...childNodes].sort((a, b) => {
      const labelA = stripHtmlFromLabel(a.data.label || "").toLowerCase();
      const labelB = stripHtmlFromLabel(b.data.label || "").toLowerCase();
      return labelA.localeCompare(labelB);
    });
  }, [childNodes]);

  const centerPosition = useMemo(() => {
    return {
      x: containerSize.width / 2,
      y: containerSize.height / 2,
    };
  }, [containerSize.height, containerSize.width]);

  const getChildPosition = (_child: CanvasNode, index: number) => {
    return computeRadialPosition(index, childNodes.length);
  };

  const handleChildClick = (child: CanvasNode) => {
    setCenterId(child.id);
  };

  const handleBreadcrumbClick = (nodeId: string) => {
    setCenterId(nodeId);
    setShowOverflowPopup(false);
  };

  const handleCenterClick = () => {
    if (!centerNode) return;
    const parentId = parentByChild.get(centerNode.id);
    if (parentId) {
      setCenterId(parentId);
    }
  };

  const filteredOverflowItems = overflowItems.filter((item) =>
    stripHtmlFromLabel(item.data.label || "")
      .toLowerCase()
      .includes(breadcrumbFilter.trim().toLowerCase()),
  );

  return (
    <div className="canvasize-container" ref={containerRef}>
      <div className="canvasize-breadcrumb" ref={breadcrumbRef}>
        {visibleBreadcrumbStart > 0 && (
          <div className="canvasize-breadcrumb-overflow">
            <span
              className="canvasize-breadcrumb-ellipsis"
              onClick={() => setShowOverflowPopup((prev) => !prev)}
              ref={ellipsisRef}
            >
              ...
            </span>
            {showOverflowPopup && (
              <div className="canvasize-breadcrumb-popup">
                <input
                  className="canvasize-breadcrumb-search"
                  placeholder="Search path..."
                  value={breadcrumbFilter}
                  onChange={(event) => setBreadcrumbFilter(event.target.value)}
                />
                <div className="canvasize-breadcrumb-list">
                  {filteredOverflowItems.map((item) => (
                    <button
                      key={item.id}
                      className="canvasize-breadcrumb-list-item"
                      onClick={() => handleBreadcrumbClick(item.id)}
                    >
                      {stripHtmlFromLabel(item.data.label || "") || "Untitled"}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {visibleBreadcrumbItems.map((item, index) => (
          <React.Fragment key={item.id}>
            {index > 0 && <span className="canvasize-breadcrumb-sep">›</span>}
            <button
              className={`canvasize-breadcrumb-item${
                item.id === centerNode?.id ? " is-active" : ""
              }`}
              onClick={() => handleBreadcrumbClick(item.id)}
            >
              {stripHtmlFromLabel(item.data.label || "") || "Untitled"}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="canvasize-canvas">
        <div className="canvasize-grid-surface">
          {centerNode && (
            <div className="canvasize-center-overlay">
              <div
                className="canvasize-card canvasize-center-card"
                onClick={handleCenterClick}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleCenterClick();
                  }
                }}
              >
                <div className="canvasize-card-header">
                  {stripHtmlFromLabel(centerNode.data.label || "") ||
                    "Untitled"}
                </div>
                {centerNode.data.notes && (
                  <div className="canvasize-card-notes">
                    {centerNode.data.notes}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="canvasize-radial">
            {sortedChildNodes.map((child, index) => {
              const position = getChildPosition(child, index);
              const typeLabel = child.data.type ? child.data.type : null;
              const markerLabel = child.data.marker ? child.data.marker : null;
              const hasImage = Boolean(child.data.object);
              const colorValue =
                child.data.backgroundColor || child.data.color || "#94a3b8";

              return (
                <div
                  key={child.id}
                  className="canvasize-card"
                  style={{
                    left: centerPosition.x + position.x,
                    top: centerPosition.y + position.y,
                  }}
                  onClick={() => handleChildClick(child)}
                >
                  <div className="canvasize-card-header">
                    <span className="canvasize-card-title">
                      {stripHtmlFromLabel(child.data.label || "") || "Untitled"}
                    </span>
                  </div>
                  {child.data.notes && (
                    <div className="canvasize-card-notes">
                      {child.data.notes}
                    </div>
                  )}
                  <div className="canvasize-card-footer">
                    {typeLabel && (
                      <span className="canvasize-badge">Type: {typeLabel}</span>
                    )}
                    {markerLabel && (
                      <span className="canvasize-badge">
                        Mark: {markerLabel}
                      </span>
                    )}
                    {hasImage && <span className="canvasize-badge">Image</span>}
                    <span className="canvasize-color-badge">
                      <span
                        className="canvasize-color-dot"
                        style={{ backgroundColor: colorValue }}
                      />
                      Color
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="canvasize-breadcrumb-measure">
        {breadcrumbItems.map((item) => (
          <span
            key={item.id}
            ref={(el) => {
              breadcrumbMeasureRefs.current[item.id] = el;
            }}
            className="canvasize-breadcrumb-item"
          >
            {stripHtmlFromLabel(item.data.label || "") || "Untitled"}
          </span>
        ))}
      </div>
    </div>
  );
};

export default CanvasizeArea;
